import GarmentChartModal from './GarmentChartModal';
import GiftCardForm from './GiftCardForm';
import ProductAsyncSwitcher from './ProductAsyncSwitcher';
import ProductBackInStock from './ProductBackInStock';
import ProductImages from './ProductImages';
import Quantity from '../Quantity';
import { closeModal } from '../../helpers/modals';
import {
  cartApi,
  getTranslation,
  readKlaviyoLearnqProduct,
  reinitOkendoWidgets,
  reinitSwymActionButtons,
  showQuickshop,
  trackKlaviyoAddedToCart,
  trackKlaviyoViewedItem,
  trackKlaviyoViewedProduct,
  updateCart,
} from '../../helpers';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  mobileBlocks: '[data-mobile-blocks]',
  form: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  floatingAtcBar: '[data-floating-atc-bar]',
  floatingAddToCart: '[data-floating-add-to-cart]',
  floatingAtcLabel: '[data-floating-atc-label]',
  productSelect: '[data-product-select]',
  optionInput: '[name^="options"]',
  modalContent: 'quickshop-content',
  modalProduct: '[data-product]',
  productImage: '[data-product-image]',
  mainCarousel: '[data-image-carousel]',
  thumbnailCarousel: '[data-thumbnail-carousel]',
  complementaryProducts: '[data-complementary-products]',
  fisTrigger: '[data-fis-open]',
  fisDrawerBackdrop: '[data-fis-drawer-backdrop]',
  fisDrawerClose: '[data-fis-drawer-close]',
};

const rootMargin = '0px 0px 200px 0px';
const limit = 4;

/**
 * The wrapper class for PDP components
 *
 * @export
 * @class Product
 */
export default class Product {
  /**
   * Creates an instance of Product.
   *
   * @param {HTMLElement} el The container of this product
   * @param {Object} config Settings object
   * @param {String} config Section ID
   */
  constructor(el, config = {}) {
    this.el = el;
    this._pruneMobileBlocks();

    this.section = this._getSectionElement(this.el);
    this.sectionId = this.section.id.replace('shopify-section-', '');
    this.config = Object.assign(
      {
        isQuickShop: false,
        isFeaturedProduct: false,
        klaviyoTrackViewedProduct: false,
        quickShopUrl: '',
        modal: null,
      },
      config,
    );

    this.formElement = this.el.querySelector(selectors.form);
    if (this.formElement) {
      this.addToCartButton = this.formElement.querySelector(
        selectors.addToCart,
      );
      this.optionInputs = this.formElement.querySelectorAll(
        selectors.optionInput,
      );
    }

    this.floatingAtcBar = this.el.querySelector(selectors.floatingAtcBar);
    this.floatingAtcButton = this.floatingAtcBar?.querySelector(
      selectors.floatingAddToCart,
    );
    this.floatingAtcLabel = this.floatingAtcBar?.querySelector(
      selectors.floatingAtcLabel,
    );

    this.complementaryProducts = this.el.querySelector(
      selectors.complementaryProducts,
    );

    this._initComponents();
    this._initFloatingAtc();

    if (this.config.klaviyoTrackViewedProduct) {
      const klaviyoItem = readKlaviyoLearnqProduct(this.el);
      trackKlaviyoViewedProduct(klaviyoItem);
      trackKlaviyoViewedItem(klaviyoItem);
    }
  }

  /**
   * Init all components that make up the product
   */
  _initComponents() {
    this.productImages = new ProductImages(this.el);
    this.productBackInStock = new ProductBackInStock(this.formElement);
    new GarmentChartModal(this.el);
    new GiftCardForm(this.el);
    this._initProductForm();
    this._initFindInStoreDrawer();

    if (this.complementaryProducts) {
      this._initComplementaryProducts();
    }

    new ProductAsyncSwitcher(this.el, this.sectionId, this.config);
  }

  _initFloatingAtc() {
    if (
      this.config.isQuickShop ||
      !this.floatingAtcBar ||
      !this.addToCartButton
    ) {
      return;
    }

    this.floatingAtcButton?.addEventListener('click', () => {
      if (this.addToCartButton.disabled) {
        this.addToCartButton.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return;
      }
      this.addToCartButton.click();
    });

    this._floatingAtcObserver = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast =
          !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.floatingAtcBar.classList.toggle('is-visible', scrolledPast);
        document.body.classList.toggle('is-floating-atc', scrolledPast);
        this.floatingAtcBar.setAttribute(
          'aria-hidden',
          scrolledPast ? 'false' : 'true',
        );
      },
      { threshold: 0, rootMargin: '0px' },
    );

    this._floatingAtcObserver.observe(this.addToCartButton);
  }

  _initProductForm() {
    this.addToCartButton?.addEventListener(
      'click',
      this._onAddToCart.bind(this),
    );

    this._initSizeSelectTrigger();

    this.optionInputs?.forEach(optionInput => {
      optionInput.addEventListener('change', this._onOptionChange.bind(this));
    });

    this._initSwatchValueHover();

    new Quantity(this.formElement);
  }

  /**
   * When `data-size-select-trigger` is on the add-to-cart control (no size yet),
   * keep it disabled and swap label to "Select a size" on hover. Listeners are
   * on the parent so hover still works while the button is disabled.
   */
  _initSizeSelectTrigger() {
    const button = this.addToCartButton;
    if (!button?.hasAttribute('data-size-select-trigger')) {
      return;
    }

    button.type = 'button';
    button.disabled = true;

    const defaultLabel = button.innerHTML;
    const wrapper = button.parentElement;
    const selectSizeText = getTranslation('products.select_size');

    const onWrapperEnter = () => {
      button.innerHTML = selectSizeText;
    };

    const onWrapperLeave = () => {
      button.innerHTML = defaultLabel;
    };

    wrapper?.addEventListener('mouseenter', onWrapperEnter);
    wrapper?.addEventListener('mouseleave', onWrapperLeave);

    button.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
    });
  }

  _initSwatchValueHover() {
    const swatchGroups = this.formElement?.querySelectorAll(
      '[data-swatch-hover-group]',
    );

    if (!swatchGroups?.length) {
      return;
    }

    swatchGroups.forEach(group => {
      const valueDisplayId = group.getAttribute('data-swatch-value-display-id');
      const valueDisplay = valueDisplayId
        ? this.formElement.querySelector(`#${valueDisplayId}`)
        : null;

      if (!valueDisplay) {
        return;
      }
      const defaultDisplayValue = valueDisplay.textContent;

      const getLabelFromEvent = target => {
        if (!(target instanceof Element)) {
          return null;
        }

        const label = target.closest('.swatch__label');
        if (!label || !group.contains(label)) {
          return null;
        }

        return label;
      };

      const updateDisplayFromLabel = label => {
        if (!(label instanceof HTMLLabelElement)) {
          return;
        }

        const input = label.querySelector('.swatch__input');
        if (!(input instanceof HTMLInputElement) || !input.value) {
          return;
        }

        valueDisplay.textContent = input.value;
      };

      const restoreDefaultValue = () => {
        valueDisplay.textContent = defaultDisplayValue;
      };

      group.addEventListener('mouseover', e => {
        const label = getLabelFromEvent(e.target);
        if (!label) {
          return;
        }

        updateDisplayFromLabel(label);
      });

      group.addEventListener('mouseout', e => {
        const label = getLabelFromEvent(e.target);
        if (!label) {
          return;
        }

        const next = e.relatedTarget;
        if (next instanceof Node && label.contains(next)) {
          return;
        }

        restoreDefaultValue();
      });

      group.addEventListener('focusin', e => {
        const input =
          e.target instanceof Element
            ? e.target.closest('.swatch__input')
            : null;
        if (!(input instanceof HTMLInputElement)) {
          return;
        }

        const label = input.closest('.swatch__label');
        updateDisplayFromLabel(label);
      });

      group.addEventListener('focusout', () => {
        restoreDefaultValue();
      });
    });
  }

  _initFindInStoreDrawer() {
    const fisTriggers = this.el.querySelectorAll(selectors.fisTrigger);
    if (!fisTriggers.length) {
      return;
    }

    const closeDrawer = drawer => {
      drawer.classList.remove('is-open');
      enablePageScroll();
    };

    const drawers = this.el.querySelectorAll('[data-fis-drawer]');
    drawers.forEach(drawer => {
      const closeButtons = drawer.querySelectorAll(
        `${selectors.fisDrawerBackdrop}, ${selectors.fisDrawerClose}`,
      );
      closeButtons.forEach(button => {
        button.addEventListener('click', () => closeDrawer(drawer));
      });
    });

    fisTriggers.forEach(trigger => {
      trigger.addEventListener('click', e => {
        e.preventDefault();
        const drawerId = trigger.getAttribute('data-fis-target');
        if (!drawerId) {
          return;
        }

        const drawer = this.el.querySelector(`#${drawerId}`);
        if (!drawer) {
          return;
        }

        drawer.classList.add('is-open');
        disablePageScroll();
      });
    });
  }

  _initComplementaryProducts() {
    this.productId = this.complementaryProducts.dataset.productId;
    // TODO: create lazy data pattern as helper
    const observer = new IntersectionObserver(
      this._fetchComplementaryProducts,
      {
        rootMargin,
      },
    );

    observer.observe(this.complementaryProducts);
  }

  _getSectionElement = el => {
    return el.closest('.shopify-section');
  };

  /**
   * Remove direct child divs without mobile-above class from data-mobile-blocks
   */
  _pruneMobileBlocks() {
    const mobileBlocks = this.el.querySelector(selectors.mobileBlocks);
    if (!mobileBlocks) {
      return;
    }

    Array.from(mobileBlocks.children).forEach(child => {
      if (
        child instanceof HTMLDivElement &&
        !child.classList.contains('mobile-above')
      ) {
        child.remove();
      }
    });
  }

  _fetchComplementaryProducts = (entries, observer) => {
    if (!entries[0].isIntersecting) {
      return;
    }

    observer.unobserve(this.complementaryProducts);
    const url = `${Shopify.routes.root}recommendations/products?section_id=${this.sectionId}&limit=${limit}&product_id=${this.productId}&intent=complementary`;

    fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const complementary = html.querySelector(
          selectors.complementaryProducts,
        );

        if (complementary && complementary.innerHTML.trim().length) {
          this.complementaryProducts.innerHTML = complementary.innerHTML;
        }
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
  };

  _onAddToCart = e => {
    e.preventDefault();

    if (this.addToCartButton?.type === 'button') {
      return;
    }

    // Respect native HTML form validation before AJAX submission.
    if (!this.formElement?.checkValidity()) {
      this.formElement?.reportValidity();
      return;
    }

    const finalSale = this.el.dataset.finalSale === 'true';
    const preorder = this.el.dataset.preorder !== undefined;
    const preorderText = this.el.dataset.preorder;

    const formData = new FormData(this.formElement);
    const data = cartApi.formatFormData(formData, {
      finalSale,
      preorder,
      preorderText,
    });

    this._toggleLoading(true, getTranslation('products.adding'));

    cartApi
      .add({ items: [data] })
      .then(response => {
        this._toggleLoading(false, getTranslation('products.added'));
        trackKlaviyoAddedToCart(readKlaviyoLearnqProduct(this.el));
        const variantSelect = this.formElement.querySelector(
          selectors.productSelect,
        );
        const refreshVariantId =
          variantSelect instanceof HTMLSelectElement ? variantSelect.value : '';
        this._reRenderSection(
          this.formElement.dataset.productUrl,
          refreshVariantId,
        );

        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }

        if (this.config.isQuickShop) {
          closeModal('quickshop');
        }
      })
      .catch(error => {
        console.error(error);
        this._toggleLoading(false, getTranslation('products.error'));
      });
  };

  _getSelectedOptions = () => {
    return Array.from(this.optionInputs)
      .reduce((arr, input) => {
        if (
          input instanceof HTMLInputElement &&
          input.type === 'radio' &&
          input.checked
        ) {
          arr.push(input.dataset.valueId);
        }

        if (input instanceof HTMLSelectElement) {
          const selectedOption = input.options[input.selectedIndex];
          arr.push(selectedOption.dataset.valueId);
        }

        return arr;
      }, [])
      .join(',');
  };

  /**
   * Section fetch: prefer ?variant= (explicit id or hidden variant select); else option_values.
   *
   * @param {string | undefined} preferredVariantId
   * @returns {string}
   */
  _resolveVariantIdForSectionRequest(preferredVariantId) {
    if (preferredVariantId) {
      return String(preferredVariantId);
    }

    const select = this.formElement?.querySelector(selectors.productSelect);
    if (select instanceof HTMLSelectElement && select.value) {
      return select.value;
    }

    return '';
  }

  _onOptionChange = e => {
    let productUrl = this.formElement.dataset.productUrl;
    let variantId;
    let isCombinedListing = false;

    if (e.target instanceof HTMLInputElement && e.target.type === 'radio') {
      // Check if changed option has associated product URL [Combined Listings]
      const { variantId: optionVariantId, productUrl: optionProductUrl } =
        e.target.dataset;

      if (optionProductUrl) {
        productUrl = optionProductUrl;
        isCombinedListing = true;
      }

      if (optionVariantId) {
        variantId = optionVariantId;
      }
    }

    if (e.target instanceof HTMLSelectElement) {
      const { productUrl: optionProductUrl, variantId: optionVariantId } =
        e.target.options[e.target.selectedIndex].dataset;

      if (optionProductUrl) {
        productUrl = optionProductUrl;
        isCombinedListing = true;
      }

      if (optionVariantId) {
        variantId = optionVariantId;
      }
    }

    let newURL;

    if (isCombinedListing) {
      newURL = productUrl;
    } else {
      newURL = new URL(window.location.href);
      newURL.searchParams.set('variant', variantId);
      newURL = newURL.toString();
    }

    this._toggleLoading(true, getTranslation('products.loading'));
    this._reRenderSection(productUrl, variantId);

    if (!this.config.isQuickShop && !this.config.isFeaturedProduct) {
      this._appendHistory(newURL);
    }
  };

  _reRenderSection = (productPath, preferredVariantId = null) => {
    let requestUrl;

    if (this.config.isQuickShop) {
      const base =
        this.formElement?.dataset.productUrl ||
        this.config.quickShopUrl ||
        productPath;
      const queryPrefix = base.includes('?') ? '&' : '?';
      requestUrl = `${base}${queryPrefix}section_id=${this.sectionId}`;
    } else {
      const queryPrefix = productPath.includes('?') ? '&' : '?';
      requestUrl = `${productPath}${queryPrefix}section_id=${this.sectionId}`;
    }

    const variantId =
      this._resolveVariantIdForSectionRequest(preferredVariantId);

    if (variantId) {
      requestUrl += `&variant=${encodeURIComponent(variantId)}`;
    } else {
      const selectedOptions = this._getSelectedOptions();
      if (selectedOptions.length) {
        requestUrl += `&option_values=${selectedOptions}`;
      }
    }

    fetch(requestUrl)
      .then(response => response.text())
      .then(data => {
        if (this.config.isQuickShop) {
          const quickshopHtml = showQuickshop(data);
          const quickShopContent = document.getElementById(
            selectors.modalContent,
          );
          quickShopContent.innerHTML = quickshopHtml;

          const product = quickShopContent.querySelector(
            selectors.modalProduct,
          );

          const form = product.querySelector(selectors.form);
          new Product(product, {
            ...this.config,
            quickShopUrl: form?.dataset.productUrl || this.config.quickShopUrl,
            klaviyoTrackViewedProduct: false,
          });
          reinitSwymActionButtons(product);
          reinitOkendoWidgets({ scope: quickShopContent });
        } else {
          this.section.outerHTML = data;
          this.section = document.getElementById(
            `shopify-section-${this.sectionId}`,
          );
          const product = this.section.querySelector('[data-product]');
          new Product(product, {
            ...this.config,
            klaviyoTrackViewedProduct: false,
          });
          reinitSwymActionButtons(product);
          reinitOkendoWidgets({ scope: this.section });
        }
      });
  };

  _appendHistory(url) {
    window.history.pushState({ path: url }, '', url);
  }

  _toggleLoading(loading = false, buttonText) {
    const atc = this.addToCartButton;
    if (loading) {
      this.formElement.classList.add('loading');
      if (atc) {
        atc.disabled = true;
      }
      this.optionInputs?.forEach(optionInput => (optionInput.disabled = true));
    } else {
      this.formElement.classList.remove('loading');
      if (atc) {
        if (!atc.hasAttribute('data-size-select-trigger')) {
          atc.removeAttribute('disabled');
        } else {
          atc.disabled = true;
        }
      }
      this.optionInputs?.forEach(optionInput =>
        optionInput.removeAttribute('disabled'),
      );
    }

    if (buttonText && atc) {
      atc.innerHTML = buttonText;
    }

    if (this.floatingAtcButton && atc) {
      this.floatingAtcButton.disabled = atc.disabled;
    }

    if (buttonText && this.floatingAtcLabel) {
      const tmp = document.createElement('div');
      tmp.innerHTML = buttonText;
      this.floatingAtcLabel.textContent = tmp.textContent ?? '';
    }
  }
}
